export interface CycleCountSessionHeader {
  cycleCountId: string;
  displayId: string;
  /** Warehouse this count belongs to (#30580: lets the detail page load the completed-count dropdown). */
  warehouseId: number;
  warehouseName: string;
  createdDate: string;
  createdBy: string;
  status: string;
  totalItems: number;
}

/** One completed count for a warehouse (#30580): a selectable entry in the detail page's variance-report
 * dropdown, which defaults to the most recent completed count. */
export interface CompletedCycleCount {
  cycleCountId: string;
  displayId: string;
  warehouseName: string;
  status: string;
  completedDate: string | null;
  totalItems: number;
}

/** Body for growing an open count (#30580): the new target count size (DB range 10–200). */
export interface AddCycleCountItemsRequest {
  newCountSize: number;
}

/** Result of growing an open count (#30580): how many items were appended and the new total. */
export interface AddCycleCountItemsResponse {
  cycleCountId: string;
  addedCount: number;
  totalItems: number;
}

export interface CycleCountSessionItem {
  cycleCountItemId: string;
  materialId: string;
  materialDescription: string;
  subLocationId: number;
  subLocationCode: string;
  subLocationName: string;
  expectedQty: number;
  actualQty: number | null;
  isDamaged: boolean;
  /** Quantity of the counted items that are broken/damaged (#30579). Null/absent when none. */
  damagedQty?: number | null;
  isNotFound: boolean;
  notes: string | null;
  variance: number | null;
  unitCost: number | null;
  /** #31393: who counted this item (returned by GetItems). Shown on the counting
   * grid so concurrent counters can see a row was done by another user (#32232). */
  countedByName?: string | null;
  /** #32233: opaque optimistic-concurrency token for this row (a string so the value survives a JS
   * round-trip without precision loss). Echo it back as {@link RecordCountRequest.expectedRowVersion} on
   * the next save; a mismatch means another counter changed the row and the save is rejected with 409. */
  rowVersion?: string | null;
}

export interface RecordCountRequest {
  actualQty: number | null;
  isDamaged: boolean;
  /** Quantity of the counted items that are broken/damaged (#30579). Null/absent when none. */
  damagedQty?: number | null;
  isNotFound: boolean;
  notes: string | null;
  /** #31393: display name (or email) of the counter, taken from the authenticated session, so the variance
   * report and count history can show who counted each item. */
  countedBy?: string | null;
  /** #32233: the {@link CycleCountSessionItem.rowVersion} the client last saw for this item. When present
   * and no longer current server-side, the save is a stale overwrite and is rejected with 409 Conflict
   * carrying the current item. Omit/null to skip the check (last-writer-wins, e.g. older callers). */
  expectedRowVersion?: string | null;
}

/** #31393: one entry in an item's count history — a distinct count event with who counted it. */
export interface CycleCountItemHistoryEntry {
  countedDate: string;
  counterName: string | null;
  actualQty: number | null;
  isNotFound: boolean;
  damagedQty: number | null;
  notes: string | null;
}

export interface SessionProgress {
  counted: number;
  total: number;
  percentage: number;
}

export interface CycleCountVarianceSummary {
  completedDate: string;
  warehouseName: string;
  totalItems: number;
  matchedCount: number;
  varianceCount: number;
  totalDollarImpact: number;
  /** #29187: Viewpoint batch id of the most recent posted adjustment batch, or null if never posted. */
  viewpointBatchId?: string | null;
  /** #29187: when this count's adjustments were last posted to Viewpoint, or null if never posted. */
  postedDate?: string | null;
}

export interface CycleCountVarianceReportItem {
  cycleCountItemId: string;
  materialId: string;
  materialDescription: string;
  subLocationCode: string;
  subLocationName: string;
  expectedQty: number;
  actualQty: number | null;
  variance: number | null;
  unitCost: number | null;
  extendedVarianceCost: number | null;
  lastCountedDate: string | null;
  /** #31393: display name (or email) of the person who counted this item. Null when not counted or counted
   * before counter capture shipped. */
  countedByName: string | null;
  notes: string | null;
  isDamaged: boolean;
  /** How many of the counted units were recorded as broken/damaged (#30579). Null/absent when none. */
  damagedQty?: number | null;
  isNotFound: boolean;
  isVariance: boolean;
  // Post-session variance review adjustment (#29394)
  isAdjusted: boolean;
  originalActualQty: number | null;
  adjustedActualQty: number | null;
  adjustedBy: string | null;
  adjustedDate: string | null;
  /** Root-cause note captured when the count was adjusted during variance review. Persisted on the item
   * (separate from {@link notes}, the count note) so it re-displays after a reload. Null when never adjusted. */
  adjustmentNote: string | null;
  /** #31918: the Viewpoint batch id of THIS row's own posted adjustment, or null/absent if this line has
   * not been posted to Viewpoint. Distinct from the report-level {@link CycleCountVarianceSummary.viewpointBatchId};
   * drives the per-row "Posted" indicator and disables the row's post checkbox. */
  viewpointBatchId?: string | null;
  /** #31918: when THIS row's adjustment was posted to Viewpoint, or null/absent if not posted. Per-row
   * counterpart to {@link CycleCountVarianceSummary.postedDate}. */
  postedDate?: string | null;
}

export interface CycleCountVarianceReport {
  summary: CycleCountVarianceSummary;
  items: CycleCountVarianceReportItem[];
}

export interface SubmitSessionResponse {
  cycleCountId: string;
  status: string;
  completedDate: string | null;
  totalItems: number;
  countedItems: number;
  notFoundItems: number;
  varianceReport: CycleCountVarianceReport | null;
}

export interface PostAdjustmentsResponse {
  batchId: string;
  itemCount: number;
  status: string;
}

export type CycleCountResearchStatus =
  | 'Pending'
  | 'Researched'
  | 'Approved'
  | 'Skip';

export interface CycleCountVarianceResearchState {
  status: CycleCountResearchStatus;
  notes: string;
}

export type CycleCountResearchMap = Record<
  string,
  CycleCountVarianceResearchState
>;

export type CycleCountAdjustmentType = 'Shortage' | 'Overage';

export interface AdjustmentItem {
  materialId: string;
  subLocationCode: string;
  expectedQty: number;
  actualQty: number;
  adjustmentQty: number;
  adjustmentType: CycleCountAdjustmentType;
  /** #29187: required root-cause note; persisted to CycleCountAdjustment.AdjustmentNote, separate from the count note. */
  adjustmentNote: string;
  /** #32048: whether the actual count was edited; the adjustment note is required only when true. */
  isAdjusted: boolean;
}

/**
 * #29187: the POST body for `/adjustments`. The backend builds an INTR nOrder from the shortage items
 * and transfers a Viewpoint MS-ticket batch. The INTR order number is operator-entered (no generator
 * exists), and the job (`xxx.NN`) is picked at post time. Items must be shortages only.
 */
export interface PostAdjustmentsRequest {
  items: AdjustmentItem[];
  orderNumber: string;
  jobNumber: string;
}
